<template>
	<ChalkyTrainerViewModalMatchConfig
		class="view-modal-global-play"
		v-model="isVisible"
		v-bind:initialConfig="initialConfig"
		v-on:submit="Handle_OnSubmit"
	/>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Global modal for match configuration and game launch.
	 * Embeds the trainer's MatchConfig modal which provides mode selection
	 * (Practice, Versus CPU, Versus Player), game settings, and multiplayer
	 * lobby functionality.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Modal/Global
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewModalGlobalPlay extends ViewBase {
		/**
		 * Initial configuration defaults based on authentication state
		 *
		 * @type Partial<Trainer.IMatchConfig>
		 */
		protected get initialConfig(): Partial<Trainer.IMatchConfig> {
			const isAuth = this.$store.getters['authentication/authenticated'];
			const user = this.$store.getters['authentication/user'];
			const playerOneName = isAuth && user?.getName() ? user.getName() : 'Player 1';

			return {
				playerAvatars: {
					playerOne: isAuth && user?.getBestAvatar() ? user.getBestAvatar().getUrl() : '',
					playerTwo: '',
				},
				playerNames: {
					playerOne: playerOneName,
					playerTwo: 'CPU',
				},
			};
		}

		/**
		 * Whether the modal is visible
		 *
		 * @type boolean
		 */
		protected isVisible: boolean = false;

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('modal:play:open', this.Handle_OnOpen);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('modal:play:open', this.Handle_OnOpen);
		}

		/**
		 * Close the modal.
		 *
		 * @return void
		 */
		protected close(): void {
			this.isVisible = false;
		}

		/**
		 * Open the modal.
		 *
		 * @return void
		 */
		protected open(): void {
			this.isVisible = true;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle modal open event from event bus.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnOpen(): Promise<void> {
			this.open();
		}

		/**
		 * Handle match configuration submission from MatchConfig modal.
		 * Stores the config in the global store and navigates to the Play page.
		 *
		 * @param Trainer.IMatchConfig config
		 * @return Promise<void>
		 */
		protected async Handle_OnSubmit(config: Trainer.IMatchConfig): Promise<void> {
			// Store the match config for the Play page to consume
			this.$store.commit('setMatchConfig', config);

			// Close modal
			this.close();

			// Navigate to Play page
			this.$router.push({ name: 'PagePlay' });
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-modal-global-play {
		// Styles inherited from ChalkyTrainerViewModalMatchConfig
	}
</style>
